import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { notifySuccess } from "../../../components/AppToast";
import { useAuth } from "../../../app/providers/AuthProvider";
import { CompanyUsersModal } from "../components/CompanyUsersModal";
import {
  useCreateCompany,
  useImpersonateCompany,
  useStaffPermissions,
  useSuperAdminCompanies,
  useUpdateCompany,
} from "../hooks";

const STATUS_VARIANTS = {
  ACTIVE: "success",
  SUSPENDED: "danger",
  PENDING: "warning",
};

// ── Alta modal ─────────────────────────────────────────────────────────────────

const ALTA_HEADING_ID = "alta-academia-modal-title";

function AltaAcademiaModal({ show, onHide }) {
  const [form, setForm] = useState({ name: "", slug_name: "", email: "" });
  const createCompany = useCreateCompany();

  function handleSubmit(e) {
    e.preventDefault();
    createCompany.mutate(form, {
      onSuccess: () => {
        notifySuccess("Academia registrada correctamente.");
        setForm({ name: "", slug_name: "", email: "" });
        onHide();
      },
    });
  }

  function patch(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  return (
    <Modal show={show} onHide={onHide} aria-labelledby={ALTA_HEADING_ID} aria-modal="true">
      <Modal.Header closeButton>
        <Modal.Title id={ALTA_HEADING_ID}>Registrar academia</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {createCompany.isError && (
          <Alert variant="danger" className="small">
            {createCompany.error?.response?.data?.detail ?? "Error al registrar la academia."}
          </Alert>
        )}
        <Form id="alta-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              required
              value={form.name}
              onChange={patch("name")}
              aria-label="Nombre de la academia"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Slug</Form.Label>
            <Form.Control
              required
              value={form.slug_name}
              onChange={patch("slug_name")}
              aria-label="Slug único de la academia"
              pattern="[a-z0-9\-]+"
              title="Solo minúsculas, números y guiones"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email del owner</Form.Label>
            <Form.Control
              type="email"
              required
              value={form.email}
              onChange={patch("email")}
              aria-label="Correo del propietario de la academia"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={createCompany.isPending}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="alta-form"
          disabled={createCompany.isPending}
          aria-label="Confirmar registro de academia"
        >
          {createCompany.isPending ? <Spinner size="sm" aria-hidden="true" /> : "Registrar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Botón suspender/reactivar ─────────────────────────────────────────────────
// Extraído porque se renderiza dos veces (celda de escritorio + tarjeta móvil):
// así el gate por rol (companies:suspend) se escribe una sola vez.

function ToggleStatusButton({ company, onToggle, disabled, className }) {
  const suspended = company.status === "SUSPENDED";
  return (
    <Button
      size="sm"
      className={className}
      style={{ minHeight: 44 }}
      variant={suspended ? "outline-success" : "outline-danger"}
      onClick={() => onToggle(company)}
      disabled={disabled}
      aria-label={
        suspended ? `Reactivar ${company.name}` : `Suspender ${company.name}`
      }
    >
      {suspended ? "Reactivar" : "Suspender"}
    </Button>
  );
}

// ── Botón ver usuarios ─────────────────────────────────────────────────────────
// Visible para cualquier rol que llegue a esta página (todos tienen `companies:
// read`) — es de solo lectura; el propio modal gatea "Activar" por separado.

function UsersButton({ company, onOpen, className }) {
  return (
    <Button
      size="sm"
      variant="outline-secondary"
      className={className}
      style={{ minHeight: 44 }}
      onClick={() => onOpen(company)}
      aria-label={`Ver usuarios de ${company.name}`}
    >
      Usuarios
    </Button>
  );
}

// ── Botón impersonar ────────────────────────────────────────────────────────
// Solo PLATFORM_ADMIN (companies:impersonate) — "ver como" el owner de la
// academia, al estilo Evazard, sin conocer/resetear su contraseña.

function ImpersonateButton({ company, className }) {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const impersonateMutation = useImpersonateCompany();

  function handleClick() {
    impersonateMutation.mutate(company.id, {
      onSuccess: async (data) => {
        await startImpersonation(data.access, data.company);
        navigate(`/${data.company.slug_name}/app/dashboard`);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message ??
          "No se pudo impersonar esta academia.";
        toast.error(msg);
      },
    });
  }

  return (
    <Button
      size="sm"
      className={className}
      style={{ minHeight: 44 }}
      variant="outline-dark"
      onClick={handleClick}
      disabled={impersonateMutation.isPending}
      aria-label={`Impersonar a ${company.name}`}
    >
      {impersonateMutation.isPending ? (
        <Spinner size="sm" aria-hidden="true" />
      ) : (
        "Impersonar"
      )}
    </Button>
  );
}

// ── AcademiesPage ──────────────────────────────────────────────────────────────

const COLUMNS = [
  { accessorKey: "name", header: "Academia" },
  { accessorKey: "slug_name", header: "Slug" },
  {
    id: "plan",
    header: "Plan",
    accessorFn: (row) => row.subscription?.plan?.name ?? "—",
  },
  {
    id: "status",
    header: "Estado",
    accessorFn: (row) => row.status ?? "—",
    cell: ({ getValue }) => {
      const v = getValue();
      return <Badge bg={STATUS_VARIANTS[v] ?? "secondary"}>{v}</Badge>;
    },
  },
  {
    accessorKey: "created_at",
    header: "Alta",
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? new Date(v).toLocaleDateString("es-CO") : "—";
    },
  },
  { id: "actions", header: "Acciones" },
];

function AcademiesPage() {
  const { data: companies, isLoading, isError } = useSuperAdminCompanies();
  const updateCompany = useUpdateCompany();
  // Gating de UX (TASK-22): el backend revalida con HasStaffPermission.
  const { can } = useStaffPermissions();
  const canCreate = can("companies", "create"); // PLATFORM_ADMIN, SALES
  const canSuspend = can("companies", "suspend"); // PLATFORM_ADMIN, SUPPORT
  const canImpersonate = can("companies", "impersonate"); // PLATFORM_ADMIN only
  const [globalFilter, setGlobalFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showAltaModal, setShowAltaModal] = useState(false);
  const [usersCompany, setUsersCompany] = useState(null);

  // "Acciones" siempre trae "Usuarios" (solo lectura, todo rol la ve); nunca
  // queda vacía aunque el rol no tenga `suspend`.
  const columns = COLUMNS;

  const filtered = useMemo(() => {
    return (companies ?? []).filter((c) => {
      const matchesPlan = !planFilter || c.subscription?.plan?.slug === planFilter;
      const matchesStatus = !statusFilter || c.status === statusFilter;
      return matchesPlan && matchesStatus;
    });
  }, [companies, planFilter, statusFilter]);

  const table = useReactTable({
    data: filtered,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 20 } },
  });

  function toggleStatus(company) {
    const nextStatus = company.status === "SUSPENDED" ? "ACTIVE" : "SUSPENDED";
    updateCompany.mutate(
      { id: company.id, status: nextStatus },
      {
        onSuccess: () =>
          notifySuccess(
            nextStatus === "SUSPENDED"
              ? `${company.name} suspendida.`
              : `${company.name} reactivada.`
          ),
      }
    );
  }

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner aria-label="Cargando academias" />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">No se pudo cargar la lista de academias.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h4 className="mb-0">Academias</h4>
        {canCreate && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowAltaModal(true)}
            aria-label="Registrar nueva academia"
          >
            + Registrar academia
          </Button>
        )}
      </div>

      {/* Filtros */}
      <Row className="g-2 mb-3">
        <Col xs={12} sm={4}>
          <Form.Control
            placeholder="Buscar por nombre o slug..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Buscar academia"
          />
        </Col>
        <Col xs={6} sm={3}>
          <Form.Select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            aria-label="Filtrar por plan"
          >
            <option value="">Todos los planes</option>
            <option value="piloto">Piloto</option>
            <option value="emprende">Emprende</option>
            <option value="academia">Academia</option>
            <option value="cadena">Cadena</option>
          </Form.Select>
        </Col>
        <Col xs={6} sm={3}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filtrar por estado"
          >
            <option value="">Todos los estados</option>
            <option value="ACTIVE">Activa</option>
            <option value="SUSPENDED">Suspendida</option>
          </Form.Select>
        </Col>
      </Row>

      {/* ── Desktop: tabla (d-none d-sm-block) ── */}
      <div className="d-none d-sm-block">
        <div className="table-responsive">
          <Table hover bordered size="sm" className="align-middle">
            <thead className="table-light">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    if (cell.column.id === "actions") {
                      return (
                        <td key={cell.id}>
                          <div className="d-flex gap-2">
                            <UsersButton company={row.original} onOpen={setUsersCompany} />
                            {canSuspend && (
                              <ToggleStatusButton
                                company={row.original}
                                onToggle={toggleStatus}
                                disabled={updateCompany.isPending}
                              />
                            )}
                            {canImpersonate && <ImpersonateButton company={row.original} />}
                          </div>
                        </td>
                      );
                    }
                    return (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Paginación */}
        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Página anterior"
          >
            ‹ Anterior
          </Button>
          <span className="small text-muted">
            Página {table.getState().pagination.pageIndex + 1} de{" "}
            {table.getPageCount()}
          </span>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Página siguiente"
          >
            Siguiente ›
          </Button>
        </div>
      </div>

      {/* ── Mobile: tarjetas (d-sm-none) ── */}
      <div className="d-sm-none">
        {table.getRowModel().rows.map((row) => {
          const c = row.original;
          return (
            <Card key={c.id} className="mb-2">
              <Card.Body className="py-2 px-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <strong>{c.name}</strong>
                    <div className="text-muted small">{c.slug_name}</div>
                  </div>
                  <Badge bg={STATUS_VARIANTS[c.status] ?? "secondary"}>{c.status}</Badge>
                </div>
                <div className="small text-muted mt-1">
                  Plan: {c.subscription?.plan?.name ?? "—"}
                </div>
                <div className="d-flex gap-2 mt-2 flex-wrap">
                  <UsersButton company={c} onOpen={setUsersCompany} />
                  {canSuspend && (
                    <ToggleStatusButton
                      company={c}
                      onToggle={toggleStatus}
                      disabled={updateCompany.isPending}
                    />
                  )}
                  {canImpersonate && <ImpersonateButton company={c} />}
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>

      {canCreate && (
        <AltaAcademiaModal show={showAltaModal} onHide={() => setShowAltaModal(false)} />
      )}
      <CompanyUsersModal company={usersCompany} onHide={() => setUsersCompany(null)} />
      <Toaster position="top-right" />
    </Container>
  );
}

export { AcademiesPage };
